import useSound from "use-sound";
import Header from "./Header/Header";
import Menu from "./Menu/Menu";
import ErrorSoundFile from "../../../Sound/Error.wav";
import useSize from "../../../Utils/useSize/useSize";
import { useEffect, useState, useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import Backend from "../../../Backend/Backend";

import Loading from "../../../Utils/Loading/Loading";
import NotFound from "../../../Utils/NotFound/NotFound";
import WatchlistCard from "./WatchlistCard/WatchlistCard";
import Marquee from "./Marquee/Marquee";

const backend = await new Backend().init();

export default function Watchlist() {
  const [play] = useSound(ErrorSoundFile);
  const windowSize = useSize();

  const invester = Number(localStorage.getItem("invester"));

  const [loading, setLoading] = useState(true);
  const [watchlist, setWatchlist] = useState([]);
  const [adv, setAdv] = useState(true);
  const [activeRowId, setActiveRowId] = useState(null);

  // ============================
  // LOAD CACHE + FETCH API
  // ============================
  useEffect(() => {
    const savedView = localStorage.getItem("watchlistView");
    if (savedView !== null) {
      setAdv(JSON.parse(savedView));
    }

    // Load cached watchlist
    const cachedWatchlist = localStorage.getItem("watchlist");

    if (cachedWatchlist) {
      setWatchlist(JSON.parse(cachedWatchlist));
      setLoading(false);
    }

    // Always refresh from API
    loadSymbols();
  }, []);

  // ============================
  // LOAD SYMBOLS FROM API
  // ============================
  const loadSymbols = useCallback(async () => {
    try {
      const data = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        user_id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        watchlist_id: "1",
      };

      const r = await backend.watchlist(data);

      setLoading(false);

      if (r.error === "False") {
        const newData = r.watchlist;

        const cached = localStorage.getItem("watchlist");

        if (
          !cached ||
          JSON.stringify(JSON.parse(cached)) !== JSON.stringify(newData)
        ) {
          setWatchlist(newData);
          localStorage.setItem("watchlist", JSON.stringify(newData));
        }
      } else {
        if (r.logout === 1) {
          window.LogOut();
        } else {
          play();
          toast.error(r.message);
        }
      }
    } catch (err) {
      console.error("Error loading symbols:", err);
      play();
      toast.error("Network error while loading watchlist");
    } finally {
      setLoading(false);
    }
  }, [play]);

  // ============================
  // TOGGLE ADV / SIMPLE VIEW
  // ============================
  const UpdateView = useCallback(() => {
    setAdv((prev) => {
      const newView = !prev;
      localStorage.setItem("watchlistView", JSON.stringify(newView));
      return newView;
    });
  }, []);

  // ============================
  // REMOVE SYMBOL
  // ============================
  const removeSymbolFromList = useCallback((symbol_id) => {
    setWatchlist((prev) => {
      const updated = prev.filter((s) => s.id !== symbol_id);

      localStorage.setItem("watchlistCache", JSON.stringify(updated));

      return updated;
    });
  }, []);

  // ============================
  // MEMOIZED WATCHLIST
  // ============================
  const renderedList = useMemo(() => {
    return watchlist.map((item, index) => (
      <WatchlistCard
        key={item.id || item.symbol_id || index}
        item={item}
        index={index + 1}
        adv={adv}
        removeSymbolFromList={removeSymbolFromList}
        activeRowId={activeRowId}
        setActiveRowId={setActiveRowId}
        invester={invester}
      />
    ));
  }, [watchlist, adv, removeSymbolFromList, activeRowId, invester]);

  const contentHeight = adv ? windowSize[1] - 90 : windowSize[1] - 120;

  return (
    <div className="flex flex-col bg-[var(--background)] text-[var(--text)]">
      {/* Header */}
      <Header invester={invester} UpdateView={UpdateView} />

      {/* Ticker */}
      <Marquee />

      {/* Menu */}
      {adv !== true && <Menu />}

      {/* Watchlist Content */}
      <div className="flex-1">
        {loading ? (
          <div className="loadingContainer">
            <Loading height={contentHeight} />
          </div>
        ) : watchlist.length > 0 ? (
          <div
            style={{
              width: windowSize[0],
              height: contentHeight,
              overflowY: "scroll",
              scrollBehavior: "smooth",
              paddingBottom: 200,
            }}
          >
            {renderedList}
          </div>
        ) : (
          <NotFound
            small
            height={contentHeight}
            title="Nothing here"
            subtext="Use the search bar to add instruments."
          />
        )}
      </div>
    </div>
  );
}
